import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// sections
import TableRow from './TableRow';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import TableInfo from '../../../components/table-info';
import Pagination from '../../../components/pagination';
import Dialog from '../../../components/dialog';
import { Message as FormMessage } from '../../../components/hook-form';
// @devextreme
import DataGrid, { Column } from 'devextreme-react/data-grid';
import DateBox from 'devextreme-react/date-box';
import { Template } from 'devextreme-react/core/template';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones, format } from '../../../utils/format';
// assets
import { ReactComponent as Create } from '../../../assets/button/create.svg';
import Calendar from '../../../assets/button/Calendar';
// styles
import { Container, SearchWrapper, Box, PrimaryButton, IconButton, Message, ButtonWrapper, TertiaryButton, FormTabs, Tabs, TempContent } from '../../../styles';
// apis
import { getMembers, deleteMembers, blockMembers, unblockMembers, unlockMembers, getTempPassword, updateExpiration } from '../../../apis';

// ----------------------------------------------------------------------

const sizes = [10, 20, 30];

const expired = [{ value: '6', name: '6개월' }, { value: '1', name: '1년' }, { value: '2', name: '2년' }, { value: 'setting', name: '직접 설정' }];

const utc = new Date(timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss'));
const min = new Date(timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss'));

// ----------------------------------------------------------------------

export default function MembersPage() {
  const mobile = useResponsive('down', 'md');

  const navigate  = useNavigate();
  const state = useLocation().state;
  
  const [ data, setData ] = useState({ content: [], totalElements: 0, totalPages: 1 });
  const { content: rows, totalElements: total, totalPages: pages } = data;

  const [ target, setTarget ] = useState('organization');
  const [ keyword, setKeyword ] = useState(state?.name || '');
  const [ page, setPage ] = useState(0);
  const [ size, setSize ] = useState(20);

  const [ dialog, setDialog ] = useState({ visible: false, type: null, title: null, id: null, identification: null });
  const [ password, setPassword ] = useState(null);
  const [ message, setMessage ] = useState(null);

  const [ form, setForm ] = useState(null);
  const closeForm = () => setForm(null);
  const [ formMessage, setFormMessage ] = useState(null);

  const [ type, setType ] = useState('setting');
  const [ date, setDate ] = useState(new Date());
  const [ difference, setDifference ] = useState(null);

  const handleClose = () => {
    setDialog({ visible: false, type: null, title: null, id: null, identification: null });
    if ( message ) setMessage(null);
    if ( password ) setPassword(null);
  };

  const handleActions = async () => {
    await action()
      .then(() => {
        handleReset();
        handleClose();
      })
      .catch(() => setMessage(`사용자 계정을 ${dialog.title}할 수 없습니다. 나중에 다시 시도해 주세요.`));
  };

  const action = async () => {
    if ( dialog.type === 'delete' ) {
      await deleteMembers(dialog.id);
    } else if ( dialog.type === 'unblock' ) {
      await unblockMembers(dialog.id);
    } else if ( dialog.type === 'block' ) {
      await blockMembers(dialog.id);
    } else if ( dialog.type === 'unlock' ) {
      await unlockMembers(dialog.id);
    }
  };

  const handleTemp = async (id: string, identification: string) => {
    setDialog({ visible: true, type: 'temp', title: '임시 비밀번호 발급', id: null, identification });
    await getTempPassword(setPassword, id)
      .catch(() => setMessage('임시 비밀번호를 발급할 수 없습니다. 나중에 다시 시도해 주세요.'));
  };

  useEffect(() => {
    if ( form ) {
      setDate(new Date(form.expiration));
      setType('setting');
    }
  }, [form]);

  useEffect(() => {
    let difference = Math.abs( utc.getTime() - date.getTime() );
    difference = Math.ceil( difference / (1000 * 60 * 60 * 24) );
    setDifference(difference);
  }, [date]);

  useEffect(() => {
    if ( form ) {
      const expirationDate = new Date(form.expiration);
  
      if ( type !== 'setting' ) {
        if ( type === '6' ) {
          const half = new Date(expirationDate).setMonth(expirationDate.getMonth() + 6);
          return setDate(new Date(half));
        }
        const year = new Date(expirationDate).setFullYear(expirationDate.getFullYear() + Number(type));
        setDate(new Date(year));
      }
    }
  }, [form, type]);

  const handleExpiration = async () => {
    if ( !moment(date).isBefore (timezones(moment().utc().format(), 'YYYY-MM-DDTHH'), 'day') ) {
      await updateExpiration(form.id, format(date, 'daily', 'end'))
        .then(() => {
          handleReset();
          closeForm();
        })
        .catch(() => setFormMessage('계정 만료일을 변경할 수 없습니다. 나중에 다시 시도해 주세요.'));
    }
  };

  useEffect(() => {
    ( async () =>
      await getMembers(setData, search(), page, size)
    )();
  }, []);

  const search = (reset?: boolean) => {
    if ( reset ) return { organization: '', identification: '' };
    return {
      organization: target === 'organization' ? keyword : '',
      identification: target === 'identification' ? keyword : ''
    }
  };

  const handlePage = (value: number) => {
    setPage(value-1);
    getMembers(setData, search(), value-1, size);
  };

  const handleSize = (value: number) => {
    setPage(0);
    setSize(value);
    getMembers(setData, search(), 0, value);
  };

  const handleSearch = () => {
    if ( keyword.length > 0 && keyword.replace(/ /g, '').length === 0 ) return;
    setPage(0);
    getMembers(setData, search(), 0, size);
  };

  const handleReset = () => {
    setKeyword('');
    setPage(0);
    getMembers(setData, search(true), 0, size);
  };

  return (
    <Container>
      <Breadcrumbs
        heading='사용자'
        links={[
          {name: '계정 관리'},
          {name: '사용자'}
        ]}
      />

      <SearchWrapper>
        <div>
          <select value={target} onChange={(event) => setTarget(event.target.value)}>
            <option value='organization'>조직</option>
            <option value='identification'>아이디</option>
          </select>

          <input type='text' placeholder='검색어를 입력하세요.' value={keyword} onChange={(event) => setKeyword(event.target.value)} onKeyDown={(event) => { if(event.key === 'Enter') handleSearch() }} />
          <PrimaryButton type='button' onClick={() => handleSearch()}>검색</PrimaryButton>
        </div>
        <div>
          <IconButton type='button' onClick={() => navigate('create')}><Create />등록</IconButton>
        </div>
      </SearchWrapper>
      
      <Box>
        <TableInfo total={total} pages={pages} page={page} sizes={sizes} size={size} handleSize={handleSize} />

        <DataGrid
          dataSource={rows}
          noDataText=''
          columnAutoWidth={true}
          paging={{enabled: false}}
          dataRowRender={({data}) => <TableRow row={data} setDialog={setDialog} handleTemp={handleTemp} setForm={setForm} />}
        >
          <Column caption='조직' dataField='organization' alignment='center' />
          <Column caption='아이디' dataField='identification' alignment='center' />
          <Column caption='이메일' dataField='email' alignment='center' />
          <Column caption='권한' dataField='role' alignment='center' />
          <Column caption='계정 상태' dataField='blocked' alignment='center' />
          <Column caption='로그인 차단' dataField='locked' alignment='center' />
          <Column caption='계정 만료일' dataField='expirationAt' alignment='center' />
          <Column caption='등록일' dataField='createdAt' alignment='center' />
          <Column />
        </DataGrid>

        <Pagination page={page} pages={pages} size={size} handlePage={handlePage} />
      </Box>

      { form &&
        <Dialog
          title='계정 만료일 변경'
          handleClose={closeForm}
        >
          <FormTabs style={{'marginBottom': '15px'}}>
            <Tabs>
            { expired.map((item) => (
              <button key={item.value} type='button' onClick={() => setType(item.value)} className={type === item.value ? 'active' : ''}>{item.name}</button>
            ))}
            </Tabs>
            <p>총 사용 기간: { difference }일</p>
          </FormTabs>

          <DateBox
            type='date'
            value={date}
            onValueChanged={(event) => setDate(event.value)}
            displayFormat='yyyy-MM-dd'
            openOnFieldClick={true}
            useMaskBehavior={true}
            pickerType={mobile ? 'rollers' : 'calendar'}
            min={min}
            disabled={type !== 'setting' ? true : false}
            dropDownButtonTemplate='calendar'
          >
            <Template name='calendar' render={Calendar} />
          </DateBox>
          { moment(date).isBefore (timezones(moment().utc().format(), 'YYYY-MM-DDTHH'), 'day') &&
            <FormMessage style={{'marginTop': '4px', 'color': '#E62E2E'}}>현재일보다 이후로 선택해 주세요.</FormMessage>
          }

          <Message $visible={formMessage ? true : false}>{ formMessage }</Message>

          <ButtonWrapper>
            <PrimaryButton onClick={() => handleExpiration()}>변경</PrimaryButton>
            <TertiaryButton onClick={() => closeForm()}>취소</TertiaryButton>
          </ButtonWrapper>
        </Dialog>
      }

      { dialog.visible &&
        <Dialog
          title={`${dialog.type !== 'temp' ? '사용자 계정 ' : ''}${dialog.title}`}
          handleClose={handleClose}
        >
          { dialog.type === 'temp' ? (
            <>
              <p>
                임시 비밀번호가 발급되었습니다.<br />
                임시 비밀번호는 발급된 시점부터 24시간만 유효합니다.<br />
                임시 비밀번호를 사용해서 로그인하신 후<br />
                바로 비밀번호를 변경하셔야 정상적으로 로그인이 가능합니다.
              </p>
              <TempContent>
                <p>
                  아이디<br />
                  임시 비밀번호
                </p>
                <span>
                  { dialog.identification }<br />
                  { password }
                </span>
              </TempContent>
            </>
          ) : (
            <p>선택하신 사용자의 계정을 {dialog.title}하시겠습니까?</p>
          )}

          <Message $visible={message ? true : false}>{ message }</Message>

          { dialog.type === 'temp' ? (
            <PrimaryButton onClick={() => handleClose()}>확인</PrimaryButton>
          ): (
            <ButtonWrapper>
              <PrimaryButton onClick={() => handleActions()}>
                { dialog.title }
              </PrimaryButton>
              <TertiaryButton onClick={() => handleClose()}>취소</TertiaryButton>
            </ButtonWrapper>
          )}

        </Dialog>
      }
    </Container>
  );
}